/**
 * Line-style icons (Feather-inspired) drawn with `currentColor` so they adopt
 * the surrounding text color: white on the dark landing nav, dark on the light
 * portal menu.
 */
import type { SVGProps } from 'react';

const base: SVGProps<SVGSVGElement> = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function OverviewIcon() {
  return (
    <svg {...base} aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function DataIntelligenceIcon() {
  return (
    <svg {...base} aria-hidden>
      <line x1="3" y1="21" x2="21" y2="21" />
      <rect x="5" y="11" width="3" height="7" rx="0.5" />
      <rect x="10.5" y="6" width="3" height="12" rx="0.5" />
      <rect x="16" y="13" width="3" height="5" rx="0.5" />
    </svg>
  );
}

function AutomationIcon() {
  return (
    <svg {...base} aria-hidden>
      <line x1="4" y1="8" x2="20" y2="8" />
      <circle cx="9" cy="8" r="2.2" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <circle cx="15" cy="16" r="2.2" />
    </svg>
  );
}

function ExpertIcon() {
  return (
    <svg {...base} aria-hidden>
      <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z" />
    </svg>
  );
}

function ConsultantIcon() {
  return (
    <svg {...base} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

function ProcessControlIcon() {
  return (
    <svg {...base} aria-hidden>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function ResearchIcon() {
  return (
    <svg {...base} aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function SubPageIcon({ slug }: { slug: string }) {
  switch (slug) {
    case 'overview':
      return <OverviewIcon />;
    case 'data-intelligence':
      return <DataIntelligenceIcon />;
    case 'automation':
      return <AutomationIcon />;
    case 'sqm-expert':
      return <ExpertIcon />;
    case 'sqm-consultant':
      return <ConsultantIcon />;
    case 'supplier-process-control':
      return <ProcessControlIcon />;
    case 'deep-research':
      return <ResearchIcon />;
    default:
      return null;
  }
}

function CapabilitiesIcon() {
  return (
    <svg {...base} aria-hidden>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

function DataHubIcon() {
  return (
    <svg {...base} aria-hidden>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
      <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </svg>
  );
}

function ContributionsIcon() {
  return (
    <svg {...base} aria-hidden>
      <circle cx="12" cy="9" r="6" />
      <path d="M9 14.5 L7.5 22 12 19 16.5 22 15 14.5" />
    </svg>
  );
}

export function NavIcon({ name }: { name: string }) {
  switch (name) {
    case 'capabilities':
      return <CapabilitiesIcon />;
    case 'data-hub':
      return <DataHubIcon />;
    case 'contributions':
      return <ContributionsIcon />;
    default:
      return null;
  }
}

/** Props builder for sized monochrome icons. */
function sized(size: number): SVGProps<SVGSVGElement> {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
}

export function IconInfo({ size = 20 }: { size?: number }) {
  return (
    <svg {...sized(size)} aria-hidden>
      <circle cx="12" cy="12" r="9.5" />
      <line x1="12" y1="11" x2="12" y2="16.5" />
      <line x1="12" y1="7.5" x2="12.01" y2="7.5" />
    </svg>
  );
}

export function IconTarget({ size = 20 }: { size?: number }) {
  return (
    <svg {...sized(size)} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconPillars({ size = 20 }: { size?: number }) {
  return (
    <svg {...sized(size)} aria-hidden>
      <polyline points="3 9 12 4 21 9" />
      <line x1="3.5" y1="21" x2="20.5" y2="21" />
      <line x1="6" y1="21" x2="6" y2="11" />
      <line x1="10" y1="21" x2="10" y2="11" />
      <line x1="14" y1="21" x2="14" y2="11" />
      <line x1="18" y1="21" x2="18" y2="11" />
    </svg>
  );
}

export function TenetIcon({ name, size = 22 }: { name: string; size?: number }) {
  const props = sized(size);
  switch (name) {
    case 'prevent':
      return (
        <svg {...props} aria-hidden>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'integration':
      return (
        <svg {...props} aria-hidden>
          <path d="M9 17H7A5 5 0 0 1 7 7h2" />
          <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      );
    case 'learn':
      return (
        <svg {...props} aria-hidden>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      );
    case 'partner':
      return (
        <svg {...props} aria-hidden>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'stability':
      return (
        <svg {...props} aria-hidden>
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
        </svg>
      );
    default:
      return null;
  }
}

/** Generic monochrome icons used across the feature sub-pages. */
export function FeatureIcon({ name, size = 20 }: { name: string; size?: number }) {
  const props = sized(size);
  switch (name) {
    case 'bar-chart':
      return (
        <svg {...props} aria-hidden>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      );
    case 'line-chart':
      return (
        <svg {...props} aria-hidden>
          <path d="M3 3v18h18" />
          <polyline points="7 14 11 10 14 13 19 7" />
        </svg>
      );
    case 'clipboard':
      return (
        <svg {...props} aria-hidden>
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <rect x="8" y="2" width="8" height="4" rx="1" />
        </svg>
      );
    case 'bot':
      return (
        <svg {...props} aria-hidden>
          <rect x="4" y="8" width="16" height="12" rx="3" />
          <circle cx="9" cy="14" r="1.3" fill="currentColor" stroke="none" />
          <circle cx="15" cy="14" r="1.3" fill="currentColor" stroke="none" />
          <line x1="12" y1="4.5" x2="12" y2="8" />
          <circle cx="12" cy="3.5" r="1.2" />
          <line x1="3" y1="13" x2="3" y2="16" />
          <line x1="21" y1="13" x2="21" y2="16" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...props} aria-hidden>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...props} aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <polyline points="12 7 12 12 16 14" />
        </svg>
      );
    case 'link':
      return (
        <svg {...props} aria-hidden>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      );
    case 'grid':
      return (
        <svg {...props} aria-hidden>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...props} aria-hidden>
          <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z" />
        </svg>
      );
    case 'zap':
      return (
        <svg {...props} aria-hidden>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'target':
      return (
        <svg {...props} aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'book':
      return (
        <svg {...props} aria-hidden>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'rocket':
      return (
        <svg {...props} aria-hidden>
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
        </svg>
      );
    case 'key':
      return (
        <svg {...props} aria-hidden>
          <circle cx="7.5" cy="15.5" r="4.5" />
          <line x1="10.7" y1="12.3" x2="20" y2="3" />
          <line x1="16.5" y1="6.5" x2="20" y2="10" />
        </svg>
      );
    default:
      return null;
  }
}
